import type {PublishingOption} from '@Components/ai-chat-window/components/publishing-options/publishing-options.types';
import type {LoadingStates} from '@Utils/loading.util';
import type {PromptBoxCoordinatorActions} from '@Components/ai-chat-window/hooks/useAiChatPromptBoxCoordinator';
import type {HistoryItem} from '@AiChat/ai-chat.types';
import type {ReactNode} from 'react';
import type {ActionTileStates} from '@Components/ai-chat-window/components/assistant-message-image-part-action-tiles/assistant-message-image-part-action-tiles.types';
import type {RecommendedTemplate} from '@Components/ai-chat-window/components/assistant-message-image-part-action-tiles/components/templates-for-inspiration-tile/templates-for-inspiration-tile.types';
import type {RecommendedTemplatesData} from '@/ai-chat/ai-chat.types';
import type {AnimationCreationState} from '@Components/ai-chat-window/components/assistant-message-image-part-animation-creations/assistant-message-image-part-animation-creations.types';
import type {MakeTextEditableLimit} from '@Components/ai-chat-window/lib/make-text-editable.library';
import type {ChatMessagePart, ChatOnLoadParams, RequestPreferences, SetChatIdNavigationOptions} from '@Components/ai-chat-window/ai-chat-window.types';

type ChatId = string;

export interface SetCurrentChatIdOptions extends SetChatIdNavigationOptions {
  messageId?: string;
}
type ImageHashedId = string;
type AnimationHashedId = string;

export interface AnimationActionsLoading {
  downloadLoadingState: LoadingStates;
  openEditorLoadingState: LoadingStates;
  loadingPublishingOption: PublishingOption | null;
  publishingLoadingState: LoadingStates;
}

export type AnimationActionsLoadingStatesMap = Record<ChatId, Record<AnimationHashedId, AnimationActionsLoading>>;

export enum AiAgentMode {
  CHAT = 'chat',
  AGENT = 'agent',
}

export interface GeneratedImagePartActionsLoading {
  downloadLoadingState: LoadingStates;
  shareLoadingState: LoadingStates;
  saveToMyDesignsLoadingState: LoadingStates;
  loadingPublishingOption: PublishingOption | null;
  publishingLoadingState: LoadingStates;
  makeTextEditableLoadingState: LoadingStates;
}

export type GeneratedImagePartActionsLoadingStatesMap = Record<ChatId, Record<ImageHashedId, GeneratedImagePartActionsLoading>>;
export type RecommendedTemplatesMap = Record<ChatId, RecommendedTemplatesData>;
export type AnimationCreationStateMap = Record<ChatId, Record<ImageHashedId, AnimationCreationState>>;

export interface AiChatContextType {
  state: {
    hasMessages: boolean;
    currentChatId?: string;
    isSidebarExpanded: boolean;
    chatHistory: HistoryItem[];
    recentChatHistory: HistoryItem[];
    isSmall: boolean;
    isPromptBoxFocused: boolean;
    hasMoreHistory: boolean;
    searchQuery: string | undefined;
    isChatHistorySearchOpen: boolean;
    credits: number;
    isUserLoggedIn: boolean;
    getLoadingStatesForImageActions: (chatId: string, imageHashedId: string) => GeneratedImagePartActionsLoading;
    getLoadingStatesForAnimationActions: (chatId: string, animationHashedId: string) => AnimationActionsLoading;
    getActionTileStates: () => ActionTileStates;
    getRecommendedTemplates: (chatId: string) => RecommendedTemplate[];
    getTemplateSearchTerm: (chatId: string) => string | null | undefined;
    getAnimationCreationState: (chatId: string, imageHashedId: string) => AnimationCreationState;
    aiChatGenerateAudio: boolean;
    isSuperUser: boolean;
    canUseEditableTextLayers: boolean;
    chargeForMakeTextEditable: boolean;
    makeTextEditableLimit: MakeTextEditableLimit;
    mode: AiAgentMode;
    showDeletedMessages: boolean;
    hideGenerationContext: boolean;
    editingUserMessageIdMap: Record<string, string | null>;
    isPaygUserWithAiChatAccess: boolean;
    isReturningUser: boolean;
    areActionTilesHidden: boolean;
    hasSeenTemplatesTile: boolean;
    isTemplateRedesignFeatureEnabled: boolean;
    promptBoxPlaceholderKey: string | null;
    isAiChatCreateAVideoFeatureEnabled: boolean;
    landingTipRefreshKey: number;
    isQuickTipsView: boolean;
    isAssetsView: boolean;
    hasGeneratedAssets: boolean;
    shouldShowAssetsIntroSubtitle: boolean | undefined;
    isChatHistorySearchLoading: boolean;
    pendingChatOnLoadParams: ChatOnLoadParams | undefined;
  };
  actions: {
    setCurrentChatId: (chatId: string | undefined, options?: SetCurrentChatIdOptions) => void;
    setCurrentChatIdInternal: (chatId: string | undefined) => void;
    setIsSidebarExpanded: (expanded: boolean) => void;
    setSearchQuery: (query: string | undefined) => void;
    openChatHistorySearch: () => void;
    closeChatHistorySearch: () => void;
    setIsPromptBoxFocused: (focused: boolean) => void;
    refetchChatHistory: () => Promise<void>;
    fetchChatHistory: (searchQuery: string | undefined, offset: number, limit: number) => Promise<HistoryItem[]>;
    refreshCredits: () => void;
    refreshMakeTextEditableLimit: () => Promise<void>;
    onLoginSuccessActions: () => void;
    updateLoadingStatesForImageActions: (chatId: string, imageHashedId: string, updatedStates: Partial<GeneratedImagePartActionsLoading>) => void;
    updateLoadingStatesForAnimationActions: (chatId: string, animationHashedId: string, updatedStates: Partial<AnimationActionsLoading>) => void;
    updateActionTileStates: (updatedStates: Partial<ActionTileStates>) => void;
    updateAnimationCreationState: (chatId: string, imageHashedId: string, updatedState: Partial<AnimationCreationState>) => void;
    setAiChatGenerateAudio: (generateAudio: boolean) => void;
    updateChatHistoryItem: (chatId: string, updates: Partial<HistoryItem>) => void;
    disconnectChatStream: (chatId: string) => void;
    registerDisconnectChatStream: (disconnectFn: (chatId: string) => void) => void;
    deleteChatHistoryItem: (chatId: string) => void;
    setIsPaygUserWithAiChatAccess: (value: boolean) => void;
    setEditingUserMessageId: (messageId: string | null, chatId?: string) => void;
    setShowDeletedMessages: (show: boolean) => void;
    setHideGenerationContext: (hide: boolean) => void;
    setMode: (mode: AiAgentMode) => void;
    activateForNewChat: () => void;
    activatePromptBox: () => void;
    registerPromptBoxCoordinator: (actions: PromptBoxCoordinatorActions | null) => void;
    navigateToQuickTips: () => void;
    navigateBack: () => void;
    navigateToAssets: () => void;
    startNewChatWithParts: (parts: ChatMessagePart[], preferences?: RequestPreferences) => void;
    clearPendingChatOnLoadParams: () => void;
    hideActionTilesPermanently: () => void;
    setSearchTagsForChat: (chatId: string, tags: string[]) => void;
    markTemplatesTileAsSeen: () => void;
    consumeAssetsIntroSubtitleView: () => void;
    openPdf: (url: string, title?: string) => void;
  };
}

/** The state half of the context — changes on every provider state update. */
export type AiChatStateContextType = AiChatContextType['state'];
/** The actions half — reference-stable, so action-only consumers don't re-render on state changes. */
export type AiChatActionsContextType = AiChatContextType['actions'];

export interface AiChatProviderProps {
  children: ReactNode;
  credits: number;
  isPaygUserWithAiChatAccess: boolean;
  isReturningUser: boolean;
  isSuperUser: boolean;
  hasGeneratedAssets: boolean;
  shouldShowAssetsIntroSubtitle?: boolean;
  isAiChatCreateAVideoFeatureEnabled: boolean;
  isTemplateRedesignFeatureEnabled: boolean;
  promptBoxPlaceholderKey: string | null;
  canUseEditableTextLayers: boolean;
  /** Client-side UX only — gates the local credit pre-check dialog. Charging is decided server-side in MakeTextEditableFacade::start(). */
  chargeForMakeTextEditable: boolean;
  makeTextEditableLimit: MakeTextEditableLimit | null;
  mode?: AiAgentMode;
}

export const AI_CHAT_SIDEBAR_EXPANDED_STATE_COOKIE = 'pmwacsec';

/** Fallback used when no initial limit is provided (anonymous / not-logged-in users). */
export const DEFAULT_MAKE_TEXT_EDITABLE_LIMIT: MakeTextEditableLimit = {
  dailyLimit: 0,
  usedToday: 0,
  remaining: 0,
  resetsAt: null,
  isLimitReached: false,
};
